import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Spinner } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useUploadQueue } from './useUploadQueue';
import type { QueueItem, QueueKind } from './types';

/**
 * 还没传上去的那一条，长得和列表里的正式记录一样（CR-037）。
 *
 * 客户原话：「点击上传后，列表是空的，需要等一会切换页面才回显示
 * （我怀疑是在上传中）……上传中是不是需要制作像其他软件一样处理中的等待页面」。
 *
 * 他猜对了。提交是**乐观**的：入队就算成功，真正传上去要等 Worker 排到它
 * （工地弱网下可能是几分钟）。而列表读的是服务端，服务端那时还没有这条记录 ——
 * 于是工人按完提交，屏幕上什么也没有发生。
 *
 * **不做「等待页面」**：整页转圈会把工人锁在原地，而这条记录可能要等到
 * 走出地下室才传得出去 —— 那会是一个转几分钟的圈。
 * 改成**把待传的那条直接摆进列表**，标上「上传中」：他看得到自己刚提交的东西
 * 确实进了系统，也照样能去干别的。传完之后这一条消失，服务端那条顶上来
 * （队列传完会让列表失效，见 App.tsx）。
 */
export function PendingCard({
  item,
  title,
  subtitle,
}: {
  item: QueueItem;
  /** 这一条是什么：日报正文、隐蔽工程部位、采购金额 */
  title: ReactNode;
  subtitle?: ReactNode;
}) {
  const { t } = useTranslation();
  const failed = item.status === 'FAILED';

  return (
    <li>
      <Card
        className={cn(
          'flex min-h-row items-center gap-3 px-4 py-3',
          failed ? 'border-danger/40 bg-danger/5' : 'border-dashed bg-surface-2/40',
        )}
      >
        <span className="shrink-0 text-ink-3">
          {failed ? <span aria-hidden>!</span> : <Spinner size={16} />}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-title">{title}</span>
          {subtitle && <span className="block truncate text-caption text-ink-3">{subtitle}</span>}
        </span>

        <span
          className={cn('shrink-0 text-caption', failed ? 'font-medium text-danger' : 'text-ink-3')}
        >
          {failed
            ? t('queue.failedShort')
            : item.attempts > 0
              ? t('queue.retrying')
              : t('queue.processing')}
        </span>
      </Card>
    </li>
  );
}

/**
 * 这一类里还没传完的条目。
 *
 * 按工地过滤：业务内容一律挂在工地下（CLAUDE.md 约束 6），
 * 在 A 工地的列表里看到 B 工地待传的采购只会让人以为记错了工地。
 * `siteId` 给 `null` 表示不按工地筛（采购是跨工地的那一个）。
 */
export function usePendingItems(kind: QueueKind, siteId: number | null): QueueItem[] {
  const queue = useUploadQueue();
  return queue.items.filter(
    (item) => item.kind === kind && (siteId === null || item.siteId === siteId),
  );
}
